import { formatMonthDay } from '@trips/shared/format';
import { programInfo } from '@trips/shared/valuations';
import { centsPerPoint, pointsValue, pointValue, redemptionProgram } from '@trips/shared/wallet';
import { selectPrefs, selectWallet } from '../../data/selectors';
import { useStore } from '../../data/store';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { TopBar } from '../../ui/TopBar';
import { CppCompare } from './CppCompare';
import s from './Wallet.module.css';
import { pts, usd } from './WalletScreen';

export function ProgramScreen({ id }: { id: string }) {
  const program = useStore((st) => st.tables.programs.get(id));
  const wallet = useStore(selectWallet);
  const prefs = useStore(selectPrefs);
  if (!program || program.deleted)
    return (
      <div>
        <TopBar backTo="/wallet" />
        <EmptyState icon="coins" title="These points aren't here" />
      </div>
    );

  const info = programInfo(program.programId);
  const mine = wallet.redemptions.filter((r) => redemptionProgram(r) === program.programId);
  const value = pointValue(program.programId, wallet.redemptions);
  const basis =
    value?.basis === 'mine'
      ? `From my ${value.count} redemption${value.count === 1 ? '' : 's'}`
      : value?.basis === 'transfers'
        ? `Moved to airlines: from my ${value.count} airline redemption${value.count === 1 ? '' : 's'}`
        : 'Published value';

  return (
    <div>
      <TopBar title={program.name} backTo="/wallet" />
      <div className={s.detail}>
        <div className={s.hero}>
          <p className={s.meta}>{program.name}</p>
          <p className={`${s.big} num`}>{pts(program.balance)}</p>
          <p className={s.meta}>
            {program.tier ? `${program.tier}. ` : ''}
            {program.checkedOn ? `Checked ${formatMonthDay(program.checkedOn, prefs)}` : ''}
          </p>
          <p className={`${s.meta} num`}>
            {program.expiresOn
              ? `Expire ${formatMonthDay(program.expiresOn, prefs)}, ${program.expiresOn.slice(0, 4)}`
              : null}
            {program.expiresOn && program.expiryNote ? ' · ' : ''}
            {program.expiryNote}
          </p>
        </div>

        {value && (
          <>
            <div className={s.facts}>
              {(
                [
                  ['Low', value.low],
                  ['Worth to me', value.main],
                  ['High', value.high],
                ] as const
              ).map(([label, cpp]) => (
                <div key={label} className={s.fact}>
                  <span className={s.factLabel}>{label}</span>
                  <span className={`${s.factValue} num`}>
                    {usd(pointsValue(program.balance, cpp))}
                  </span>
                  <span className={`${s.factLabel} num`}>{cpp.toFixed(1)}¢ a point</span>
                </div>
              ))}
            </div>
            <p className={s.meta}>{basis}</p>
          </>
        )}

        {info && info.sweetSpots.length > 0 && (
          <section className={s.section}>
            <h2 className={s.sectionHead}>Best ways to use them</h2>
            <div className={s.rows}>
              {info.sweetSpots.map((x) => (
                <div key={x.title} className={s.row}>
                  <span className={s.rowText}>
                    <span className={s.rowTitle}>{x.title}</span>
                    {x.note && <span className={s.rowDetail}>{x.note}</span>}
                  </span>
                  <span className={`${s.rowValue} num`}>
                    {x.cppLow}–{x.cppHigh}¢
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {info?.transfersTo && info.transfersTo.length > 0 && (
          <section className={s.section}>
            <h2 className={s.sectionHead}>Transfers to</h2>
            <div className={s.rows}>
              {info.transfersTo.map((t) => (
                <div key={t} className={s.row}>
                  <span className={s.rowDetail}>{t}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {mine.length > 0 && (
          <section className={s.section}>
            <h2 className={s.sectionHead}>My redemptions</h2>
            <div className={s.rows}>
              {mine.map((r) => {
                const cpp = centsPerPoint(r);
                return (
                  <button
                    key={r.id}
                    type="button"
                    className={s.row}
                    onClick={() => navigate(`/wallet/edit/redemption/${r.id}`, { overlay: true })}
                  >
                    <span className={s.rowText}>
                      <span className={s.rowTitle}>{r.title}</span>
                      <span className={`${s.rowDetail} num`}>
                        {pts(r.points)} points, {formatMonthDay(r.on, prefs)}
                      </span>
                    </span>
                    {cpp != null && <CppCompare cpp={cpp} programId={program.programId} />}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {program.notes && <p className={s.notes}>{program.notes}</p>}
        {info && <p className={s.meta}>Values: {info.source}</p>}
        <div className={s.actions}>
          <Button
            icon="pencil-simple"
            onClick={() => navigate(`/wallet/edit/program/${program.id}`, { overlay: true })}
          >
            Update balance
          </Button>
          <Button
            variant="ghost"
            icon="plus"
            onClick={() =>
              navigate(`/wallet/new/redemption?program=${program.programId}`, { overlay: true })
            }
          >
            Add redemption
          </Button>
          <Button
            variant="ghost"
            icon="chart-line-up"
            onClick={() => navigate(`/wallet/history?entry=${program.id}`)}
          >
            Over time
          </Button>
        </div>
      </div>
    </div>
  );
}
